import React, { useEffect, useState, useCallback } from 'react';
import EventTraceabilityStatus from '../../components/traceability/EventTraceabilityStatus';
import { useNavigate } from 'react-router-dom';
import { eventsAPI, clientsAPI } from '../../api/client';
import { EVENT_TYPES, EVENT_STATUS, RISK_LEVEL, getDaysLabel } from '../../lib/eventConstants';
import { useAuth } from '../../context/AuthContext';
import {
  Plus, Search, Calendar, Users, LayoutGrid, List,
  X, ChevronLeft, ChevronRight, AlertTriangle, CheckCircle, Circle,
  Briefcase, Building2, ClipboardList,
} from 'lucide-react';
import { toast } from 'sonner';
import ArchiveButton from '../../components/ArchiveButton';

function EventStatusBadge({ status }) {
  const cfg = EVENT_STATUS[status] || EVENT_STATUS.planeacion;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${cfg.bg}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function RiskBadge({ level }) {
  const cfg = RISK_LEVEL[level] || RISK_LEVEL.medio;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold border ${cfg.bg}`}>
      {cfg.label}
    </span>
  );
}

function EventCard({ event, onView, onEdit, canEdit, isAdmin, onArchived }) {
  const et = EVENT_TYPES[event.event_type] || { label: event.event_type, emoji: '📅' };
  const dates = event.dates || {};
  const daysLabel = getDaysLabel(dates.event_start);
  const loc = event.location;

  return (
    <div className="soem-card hover:shadow-md transition-shadow overflow-hidden" data-testid={`event-card-${event.id}`}>
      {/* Top bar */}
      <div className="px-4 pt-4 pb-2 flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <EventStatusBadge status={event.status} />
          <RiskBadge level={event.projected_risk_level} />
        </div>
        <span className="text-slate-300 text-xs font-mono flex-shrink-0">{event.event_code}</span>
      </div>

      {/* Name and type */}
      <div className="px-4 pb-3">
        <h3 className="font-bold text-soem-navy text-base leading-tight">{event.name}</h3>
        <span className="inline-flex items-center gap-1 text-xs text-slate-500 mt-0.5">
          <span>{et.emoji}</span> {et.label}
        </span>
      </div>

      {/* Cliente + Proyecto (#EventListCounts) — antes sólo se veía el
          nombre del evento, ahora cada tarjeta muestra a qué cliente y
          proyecto pertenece para que el lider no tenga que abrir cada uno. */}
      <div className="px-4 pb-2 space-y-1">
        {event.client_name && (
          <div className="flex items-center gap-1.5 text-xs text-slate-600">
            <Building2 size={11} className="text-slate-400 flex-shrink-0" />
            <span className="truncate font-medium">{event.client_name}</span>
          </div>
        )}
        {event.project_name && (
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <Briefcase size={11} className="text-slate-400 flex-shrink-0" />
            <span className="truncate">{event.project_name}</span>
          </div>
        )}
      </div>

      {/* Location */}
      {loc && (
        <div className="px-4 pb-2 flex items-center gap-1 text-xs text-slate-500">
          <span>📍</span>
          <span className="truncate">{loc.name}{loc.address?.city ? ` · ${loc.address.city}` : ''}</span>
        </div>
      )}

      {/* Dates */}
      <div className="px-4 pb-2 space-y-0.5">
        {dates.event_start && (
          <p className="text-xs text-slate-500 flex items-center gap-1">
            <Calendar size={11} />
            {new Date(dates.event_start).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </p>
        )}
        {daysLabel && (
          <p className={`text-xs font-semibold ${daysLabel.color}`}>{daysLabel.text}</p>
        )}
        {/* #EventCreatedAt — fecha de creación del registro */}
        {event.created_at && (
          <p className="text-[11px] text-slate-400 flex items-center gap-1 mt-1">
            <span>🕒</span>
            Creado {new Date(event.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}
          </p>
        )}
      </div>

      {/* Evaluaciones realizadas vs pendientes (#EventListCounts) */}
      <div className="px-4 pb-2 flex items-center gap-2">
        <ClipboardList size={11} className="text-slate-400 flex-shrink-0" />
        <span className="text-xs text-slate-500">
          <span className="font-semibold text-green-700">{event.evaluations_done ?? 0}</span> realizadas
          <span className="text-slate-300 mx-1">·</span>
          <span className="font-semibold text-orange-600">{event.evaluations_pending ?? 0}</span> pendientes
        </span>
      </div>

      {/* Aforo + checklists */}
      <div className="px-4 pb-3 flex items-center gap-4">
        {event.expected_attendance && (
          <span className="text-xs text-slate-500 flex items-center gap-1">
            <Users size={11} />
            {event.expected_attendance.toLocaleString()}
          </span>
        )}
        <div className="ml-auto"><EventTraceabilityStatus event={event} /></div>
      </div>

      {/* Actions */}
      <div className="border-t border-slate-100 px-4 py-3 flex items-center gap-2">
        <button onClick={() => onView(event.id)}
          className="soem-btn-primary flex-1 py-1.5 text-xs" data-testid={`view-${event.id}`}>
          Ver detalle
        </button>
        {canEdit && (
          <button onClick={() => onEdit(event.id)}
            className="soem-btn-secondary px-3 py-1.5 text-xs" data-testid={`edit-${event.id}`}>
            Editar
          </button>
        )}
        {isAdmin && <ArchiveButton entity="events" id={event.id} name={event.name} onArchived={onArchived} />}
      </div>
    </div>
  );
}

function UpcomingBanner({ events, onNavigate }) {
  const [idx, setIdx] = useState(0);
  if (!events.length) return null;
  // #A12 — `idx` nunca se acotaba cuando `events` se encogía (ej. el
  // usuario navega el carrusel a la posición 3 y luego aplica un
  // filtro que deja solo 1 evento próximo): events[idx] daba undefined
  // y `e.dates` reventaba con TypeError, tumbando todo el Directorio de
  // Eventos via el ErrorBoundary.
  const safeIdx = Math.min(idx, events.length - 1);
  const e = events[safeIdx];
  const dates = e.dates || {};
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-sm px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-blue-100 transition-colors"
      onClick={() => onNavigate(e.id)}
      data-testid="upcoming-event-banner">
      <div className="flex items-center gap-3">
        <AlertTriangle size={16} className="text-blue-600 flex-shrink-0" />
        <p className="text-blue-800 text-sm">
          <strong>Próximo evento:</strong> {e.name}
          {dates.event_start && ` — ${new Date(dates.event_start).toLocaleDateString('es-MX')}`}
          {e.location && ` en ${e.location.name}`}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {events.length > 1 && (
          <div className="flex items-center gap-1">
            <button onClick={(ev) => { ev.stopPropagation(); setIdx((safeIdx - 1 + events.length) % events.length); }}
              className="p-1 text-blue-400 hover:text-blue-600"><ChevronLeft size={14} /></button>
            <span className="text-xs text-blue-500">{safeIdx + 1}/{events.length}</span>
            <button onClick={(ev) => { ev.stopPropagation(); setIdx((safeIdx + 1) % events.length); }}
              className="p-1 text-blue-400 hover:text-blue-600"><ChevronRight size={14} /></button>
          </div>
        )}
        <span className="text-xs text-blue-600 font-semibold ml-2">Ver evento →</span>
      </div>
    </div>
  );
}

export default function EventsList() {
  const navigate = useNavigate();
  const { isAdmin, isLider } = useAuth();
  const canEdit = isAdmin || isLider;

  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(() => localStorage.getItem('evt_view') || 'cards');
  // #EventListCounts — filtro por cliente. client_id viaja al backend
  // como query param (la API ya lo soportaba via list_events).
  const [filters, setFilters] = useState({ q: '', event_type: '', risk_level: '', status: '', client_id: '' });
  const [clientsOpts, setClientsOpts] = useState([]);

  // Cargar lista de clientes una sola vez para el dropdown del filtro.
  useEffect(() => {
    clientsAPI.list()
      .then((r) => {
        const list = r.data?.items || r.data || [];
        // Ordenar alfabéticamente, sólo clientes activos
        const sorted = list
          .filter((c) => c.is_active !== false)
          .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es'));
        setClientsOpts(sorted);
      })
      .catch(() => { /* silencioso — el filtro queda vacío si falla */ });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.event_type) params.event_type = filters.event_type;
      if (filters.status) params.status = filters.status;
      if (filters.risk_level) params.risk_level = filters.risk_level;
      if (filters.client_id) params.client_id = filters.client_id;
      const [evtsRes, statsRes] = await Promise.all([
        eventsAPI.list(params),
        eventsAPI.stats(),
      ]);
      // #ChronoOrder — más reciente arriba. Backend ya manda sorted, pero
      // garantizamos client-side por si llega cacheado o desordenado.
      const items = (evtsRes.data.items || [])
        .filter(i => !i.is_archived)
        .sort((a, b) => {
          const ad = a.created_at ? new Date(a.created_at).getTime() : 0;
          const bd = b.created_at ? new Date(b.created_at).getTime() : 0;
          return bd - ad;
        });
      setEvents(items);
      setStats(statsRes.data);
    } catch { toast.error('Error al cargar eventos'); }
    finally { setLoading(false); }
  }, [filters.event_type, filters.status, filters.risk_level, filters.client_id]);

  useEffect(() => { load(); }, [load]);

  const changeView = (v) => { setView(v); localStorage.setItem('evt_view', v); };
  const clearFilters = () => setFilters({ q: '', event_type: '', risk_level: '', status: '', client_id: '' });
  const hasFilters = filters.q || filters.event_type || filters.risk_level || filters.status || filters.client_id;

  // Client-side name filter
  const filtered = filters.q
    ? events.filter(e => e.name.toLowerCase().includes(filters.q.toLowerCase()) || (e.event_code || '').toLowerCase().includes(filters.q.toLowerCase()))
    : events;

  // Upcoming (next 7 days)
  const now = new Date();
  const upcoming7 = events.filter(e => {
    const start = e.dates?.event_start ? new Date(e.dates.event_start) : null;
    if (!start) return false;
    const diff = (start - now) / (1000 * 60 * 60 * 24);
    return diff >= 0 && diff <= 7;
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="events-list">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-soem-navy">Directorio de Eventos</h1>
          <p className="text-slate-500 text-sm mt-1">{events.length} eventos registrados</p>
        </div>
        {canEdit && (
          <button onClick={() => navigate('/dashboard/events/new')}
            className="soem-btn-primary px-4 py-2 text-sm flex items-center gap-2"
            data-testid="new-event-btn">
            <Plus size={16} /> Nuevo Evento
          </button>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Eventos', value: stats.total_events, color: '#1A56DB' },
            { label: 'En Planeación', value: stats.by_status?.planeacion || 0, color: '#3B82F6' },
            { label: 'En Curso', value: stats.by_status?.en_curso || 0, color: '#15803D' },
            { label: 'Próximos 30 días', value: stats.upcoming_count || 0, color: '#D97706' },
          ].map(s => (
            <div key={s.label} className="soem-card p-5 border-t-4" style={{ borderTopColor: s.color }}>
              <p className="soem-label mb-1">{s.label}</p>
              <p className="text-3xl font-black text-soem-navy">{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Upcoming banner */}
      <UpcomingBanner events={upcoming7} onNavigate={(id) => navigate(`/dashboard/events/${id}`)} />

      {/* Filters */}
      <div className="soem-card p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-48">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input className="pl-9 pr-4 py-2 border border-slate-300 rounded-sm text-sm w-full focus:outline-none focus:ring-2 focus:ring-soem-primary"
              placeholder="Buscar eventos..." aria-label="Buscar eventos" value={filters.q}
              onChange={e => setFilters(f => ({ ...f, q: e.target.value }))} data-testid="evt-search" />
          </div>
          {/* #EventListCounts — filtro por cliente */}
          <select className="px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
            value={filters.client_id} aria-label="Filtrar por cliente"
            onChange={e => setFilters(f => ({ ...f, client_id: e.target.value }))}>
            <option value="">Todos los clientes</option>
            {clientsOpts.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <select className="px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
            value={filters.event_type} aria-label="Filtrar por tipo de evento" onChange={e => setFilters(f => ({ ...f, event_type: e.target.value }))}>
            <option value="">Todos los tipos</option>
            {Object.entries(EVENT_TYPES).map(([k, v]) => (
              <option key={k} value={k}>{v.emoji} {v.label}</option>
            ))}
          </select>
          <select className="px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
            value={filters.risk_level} aria-label="Filtrar por nivel de riesgo" onChange={e => setFilters(f => ({ ...f, risk_level: e.target.value }))}>
            <option value="">Todos los riesgos</option>
            {Object.entries(RISK_LEVEL).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select className="px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
            value={filters.status} aria-label="Filtrar por estado" onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
            <option value="">Todos los estados</option>
            {Object.entries(EVENT_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          {hasFilters && (
            <button aria-label="Cerrar" onClick={clearFilters} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-soem-primary">
              <X size={14} /> Limpiar
            </button>
          )}
          <div className="flex items-center gap-1 ml-auto border border-slate-300 rounded-sm overflow-hidden">
            <button onClick={() => changeView('cards')}
              className={`p-2 ${view === 'cards' ? 'bg-soem-primary text-white' : 'text-slate-400 hover:bg-slate-50'}`}>
              <LayoutGrid size={15} />
            </button>
            <button onClick={() => changeView('table')}
              className={`p-2 ${view === 'table' ? 'bg-soem-primary text-white' : 'text-slate-400 hover:bg-slate-50'}`}>
              <List size={15} />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[...Array(3)].map((_, i) => <div key={i} className="soem-card h-64 animate-pulse bg-slate-100" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="soem-card p-16 text-center">
          <Calendar size={48} className="text-slate-200 mx-auto mb-4" />
          <p className="text-slate-500 font-medium">
            {hasFilters ? 'No se encontraron eventos con esos filtros.' : 'No hay eventos registrados.'}
          </p>
          {canEdit && !hasFilters && (
            <button onClick={() => navigate('/dashboard/events/new')}
              className="soem-btn-primary px-5 py-2 text-sm mt-4 inline-flex items-center gap-2">
              <Plus size={14} /> Crear primer evento
            </button>
          )}
        </div>
      ) : view === 'cards' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map(evt => (
            <EventCard key={evt.id} event={evt} canEdit={canEdit} isAdmin={isAdmin}
              onView={id => navigate(`/dashboard/events/${id}`)}
              onEdit={id => navigate(`/dashboard/events/${id}/edit`)}
              onArchived={load} />
          ))}
        </div>
      ) : (
        <div className="soem-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="soem-table-header">
                <th className="px-4 py-3 text-left">Código</th>
                <th className="px-4 py-3 text-left">Nombre</th>
                <th className="px-4 py-3 text-left">Cliente</th>
                <th className="px-4 py-3 text-left">Proyecto</th>
                <th className="px-4 py-3 text-left">Tipo</th>
                <th className="px-4 py-3 text-left">Locación</th>
                <th className="px-4 py-3 text-left">Fecha</th>
                <th className="px-4 py-3 text-left">Creado</th>
                <th className="px-4 py-3 text-left">Días</th>
                <th className="px-4 py-3 text-left">Riesgo</th>
                <th className="px-4 py-3 text-left">Estatus</th>
                <th className="px-4 py-3 text-left">Evaluaciones</th>
                <th className="px-4 py-3 text-left">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(evt => {
                const et = EVENT_TYPES[evt.event_type] || { emoji: '📅', label: evt.event_type };
                const dates = evt.dates || {};
                const dl = getDaysLabel(dates.event_start);
                return (
                  <tr key={evt.id} className="soem-table-row" data-testid={`evt-row-${evt.id}`}>
                    <td className="px-4 py-3 font-mono text-xs text-slate-400">{evt.event_code}</td>
                    <td className="px-4 py-3 font-semibold text-soem-navy">{evt.name}</td>
                    <td className="px-4 py-3 text-slate-600 text-xs">{evt.client_name || '—'}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{evt.project_name || '—'}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{et.emoji} {et.label}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{evt.location?.name || '—'}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">
                      {dates.event_start ? new Date(dates.event_start).toLocaleDateString('es-MX') : '—'}
                    </td>
                    {/* #EventCreatedAt — fecha de creación del registro */}
                    <td className="px-4 py-3 text-slate-400 text-xs">
                      {evt.created_at ? new Date(evt.created_at).toLocaleDateString('es-MX') : '—'}
                    </td>
                    <td className={`px-4 py-3 text-xs font-semibold ${dl?.color || ''}`}>{dl?.text || '—'}</td>
                    <td className="px-4 py-3"><RiskBadge level={evt.projected_risk_level} /></td>
                    <td className="px-4 py-3"><EventStatusBadge status={evt.status} /></td>
                    <td className="px-4 py-3 text-xs">
                      <span className="font-semibold text-green-700">{evt.evaluations_done ?? 0}</span>
                      <span className="text-slate-400 mx-1">/</span>
                      <span className="font-semibold text-orange-600">{evt.evaluations_pending ?? 0}</span>
                      <div className="text-slate-400 text-[10px] mt-0.5">realizadas / pendientes</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => navigate(`/dashboard/events/${evt.id}`)}
                          className="text-soem-primary text-xs hover:underline">Ver</button>
                        {canEdit && (
                          <button onClick={() => navigate(`/dashboard/events/${evt.id}/edit`)}
                            className="text-slate-400 text-xs hover:text-soem-primary">Editar</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
